import { useAuth, SignInButton, SignUpButton } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next'; // Import useTranslation and Trans
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';
import Bars3Icon from './icons/Bars3Icon';
import CookieConsentBanner from './CookieConsentBanner';
import Logo from './Logo';
import XMarkIcon from './icons/XMarkIcon';
import ShareIcon from './icons/ShareIcon';
import XSocialIcon from './icons/XSocialIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import ChatBubbleLeftRightIcon from './icons/ChatBubbleLeftRightIcon';
import UserCircleIcon from './icons/UserCircleIcon';
import LinkedInIcon from './icons/LinkedInIcon';
import CheckIcon from './icons/CheckIcon';
import ChevronLeftIcon from './icons/ChevronLeftIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';
import FaqItem from './FaqItem';
import { testimonials } from '@/src/locales/testimonialData'; // Import testimonials
import { useCallback, useEffect, useState, useRef } from 'react';
import { QuestionAnswerOption } from '@/types';
import { UI_LANGUAGES } from '@/constants';

// Definicija za testimonial karticu da izbegnemo ponavljanje koda
interface TestimonialData {
  quoteKey: string;
  authorKey: string;
  roleKey: string;
  avatarUrl: string;
}

const TestimonialCard: React.FC<{ testimonial: TestimonialData }> = ({ testimonial }) => {
    const { t } = useTranslation();

    return (
        <div className="bg-white p-8 rounded-xl shadow-lg flex flex-col h-full">
            <div className="flex-shrink-0">
                <img src={testimonial.avatarUrl} alt={`Avatar of ${t(testimonial.authorKey)}`} className="w-20 h-20 rounded-full mx-auto mb-6 border-4 border-sky-100" />
            </div>
            <div className="flex-grow flex flex-col justify-center">
                <blockquote className="text-lg text-gray-600 italic text-center relative pt-4 pb-4">
                    <span className="absolute -top-2 left-0 text-8xl text-sky-100 font-serif leading-none" aria-hidden="true">“</span>
                    <p className="relative z-10">{t(testimonial.quoteKey)}</p>
                    <span className="absolute -bottom-10 right-0 text-8xl text-sky-100 font-serif leading-none" aria-hidden="true">”</span>
                </blockquote>
            </div>
            <div className="mt-8 text-center flex-shrink-0">
                <p className="font-bold text-lg text-gray-800">{t(testimonial.authorKey)}</p>
                <p className="text-md text-sky-600 font-medium">{t(testimonial.roleKey)}</p>
            </div>
        </div>
    );
};

interface PricingCardProps {
  titleKey: string;
  price: string;
  creditsKey: string;
  descriptionKey: string;
  buttonTextKey: string;
  primary?: boolean;
  onButtonClick?: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({ titleKey, price, creditsKey, descriptionKey, buttonTextKey, primary = false }) => {
  const { t } = useTranslation();
  const cardId = `pricing-card-${t(titleKey).replace(/\s+/g, '-').toLowerCase()}`;
  const buttonAriaLabel = `${t(buttonTextKey)} ${t(titleKey)} ${t('pricing.plan_suffix')}`;

  return (
    <div className={`relative border rounded-xl p-8 text-center flex flex-col transition-all duration-300 ${primary ? 'bg-sky-600 text-white border-sky-700 shadow-2xl md:scale-105' : 'bg-white text-gray-800 hover:shadow-lg hover:-translate-y-1'}`} aria-labelledby={cardId}>
      {primary && <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 font-semibold px-4 py-1 rounded-full text-sm shadow-md">{t('pricing.best_choice')}</div>}
      <h3 id={cardId} className="text-2xl font-bold mt-4">{t(titleKey)}</h3>
      <p className={`text-4xl font-extrabold my-4 ${primary ? 'text-white' : 'text-sky-600'}`}>
        <span className="sr-only">{t('pricing.price_label')}:</span>
        {price}
      </p>
      <p className="font-semibold">{t(creditsKey)}</p>
      <p className="text-sm mt-2 mb-6 flex-grow">{t(descriptionKey)}</p>
      <SignUpButton mode="modal">
        <Button size="lg" className={primary ? 'bg-white !text-sky-700 hover:!text-white' : 'bg-sky-600 text-white hover:bg-sky-700'} aria-label={buttonAriaLabel}>
          {t(buttonTextKey)}
        </Button>
      </SignUpButton>
    </div>
  );
};

const rotatingTextsKeys = [
  "hero.rotating_text_1",
  "hero.rotating_text_2",
  "hero.rotating_text_3",
  "hero.rotating_text_4",
  "hero.rotating_text_5",
  "hero.rotating_text_6",
  "hero.rotating_text_7",
  "hero.rotating_text_8"
];

const comparisonDataKeys = [
    { featureKey: 'comparison.feature_1', klikero: '✔️', chatGPT: '❌' },
    { featureKey: 'comparison.feature_2', klikero: '✔️', chatGPT: '❌' },
    { featureKey: 'comparison.feature_3', klikero: '✔️', chatGPT: '❌' },
    { featureKey: 'comparison.feature_4', klikero: '✔️', chatGPT: '❌' },
    { featureKey: 'comparison.feature_5', klikero: '✔️', chatGPT: '❌' },
    { featureKey: 'comparison.feature_6', klikero: '✔️', chatGPT: '❌' },
];

// const techLogos = [
//   { name: 'Google AI', logoUrl: '/logos/google-ai.svg' },
//   { name: 'React', logoUrl: '/logos/react.svg' },
//   { name: 'Clerk', logoUrl: '/logos/clerk.svg' },
//   { name: 'Vercel', logoUrl: '/logos/vercel.svg' },
//   { name: 'i18next', logoUrl: '/logos/i18next.svg' },
//   { name: 'Tailwind CSS', logoUrl: '/logos/tailwind-css.svg' },
// ];

const LandingPage: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  // State for interactive sharing demo
  const [isShared, setIsShared] = useState(false);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  const currentLanguage = UI_LANGUAGES.find(lang => i18n.language.startsWith(lang.code)) || UI_LANGUAGES[0];

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const faqData = t('helperMenu.faq', { returnObjects: true }) as Record<string, string>;
  const faqOptions: QuestionAnswerOption[] = Object.keys(faqData)
    .filter(key => key.startsWith('q'))
    .map(qKey => parseInt(qKey.substring(1)))
    .map(num => ({ question: faqData[`q${num}`], answer: faqData[`a${num}`] }));

  const handleShareClickDemo = () => {
    if (isShared) return;
    setIsShared(true);
    setTimeout(() => setIsShared(false), 2500); // Reset after 2.5 seconds
  };

  const nextTestimonial = useCallback(() => {
    setActiveTestimonial(prev => (prev + 1) % testimonials.length);
  }, []);

  const prevTestimonial = useCallback(() => {
    setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    const testimonialTimer = setInterval(nextTestimonial, 7000); // Auto-scroll every 7 seconds
    return () => clearInterval(testimonialTimer);
  }, [nextTestimonial]);

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate('/app', { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      // Menja indeks na sledeći tekst, i vraća se na 0 kada dođe do kraja
      setCurrentIndex(prevIndex => (prevIndex + 1) % rotatingTextsKeys.length);
    }, 5000); // Poruka se menja na svakih 5 sekundi

    // Očisti tajmer kada se komponenta uništi da se izbegne curenje memorije
    return () => clearInterval(timer);
  }, []); // Prazan niz osigurava da se useEffect pokrene samo jednom

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicks outside of language menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  useEffect(() => {
    // Set language from browser on initial visit, if not already set.
    const storedLang = i18n.services.languageDetector.detect();
    if (!storedLang || (Array.isArray(storedLang) && storedLang.length === 0)) {
      const browserLang = navigator.language.split('-')[0];
      const isSupported = UI_LANGUAGES.some(lang => lang.code === browserLang);
      if (isSupported) {
        i18n.changeLanguage(browserLang);
      }
    }
  }, [i18n]);

  if (!isLoaded || isSignedIn) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  // CSS za animaciju direktno u komponenti
  const animationStyle = `
    html {
      scroll-behavior: smooth;
      scroll-padding-top: 80px; /* Adjust for sticky header height */
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fadeInUp {
      animation: fadeInUp 0.8s ease-in-out forwards;
    }
    @keyframes shimmer {
      100% { background-position: 200% center; }
    }
    .animate-shimmer {
      animation: shimmer 4s infinite linear;
    }
    .perspective-1000 {
      perspective: 1000px;
    }
    @keyframes infinite-scroll {
      from { transform: translateX(0); }
      to { transform: translateX(-100%); }
    }
    .animate-infinite-scroll {
      animation: infinite-scroll 40s linear infinite;
    }
  `;

  const navLinks = [
    { href: '#how-it-works', labelKey: 'navbar.how_it_works' },
    { href: '#testimonials', labelKey: 'navbar.testimonials' },
    { href: '#pricing', labelKey: 'navbar.pricing' },
    { href: '#faq', labelKey: 'navbar.faq' },
    { href: '#contact', labelKey: 'navbar.contact' },
  ];

  const handleMobileLinkClick = () => {
    setIsMenuOpen(false);
    // The browser will handle the smooth scroll via CSS.
  };

  return (
    <div className="bg-white text-gray-800">
      <style>{animationStyle}</style>
      <CookieConsentBanner />

      {/* Header Section */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/80 backdrop-blur-sm shadow-md border-b border-slate-200' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              textClassName={isScrolled ? 'text-sky-700' : 'text-white'}
            />
            <nav className="hidden md:flex items-center space-x-6">
              {navLinks.map(link => (
                <a key={link.href} href={link.href} className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-sky-600' : 'text-white hover:text-gray-200'}`}>
                  {t(link.labelKey)}
                </a>
              ))}
            </nav>
            <div className="flex items-center space-x-2">
              <div className="hidden md:flex items-center space-x-2">
                <SignInButton mode="modal">
                  <Button variant={isScrolled ? 'ghost' : 'secondary'} className={!isScrolled ? '!text-white !bg-white/20 hover:!bg-white/30' : ''}>
                    {t('auth.login')}
                  </Button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <Button className={!isScrolled ? 'hover:bg-white hover:text-sky-600 font-bold hover:bg-gray-100' : ''}>
                    {t('auth.register')}
                  </Button>
                </SignUpButton>
              </div>
                {/* Language Switcher */}
                <div className="relative" ref={langMenuRef}>
                  <Button
                    onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                    variant="ghost"
                    size="icon"
                    aria-label="Change language"
                    className={`ml-2 !w-10 !h-10 ${!isScrolled ? '!text-white !bg-white/20 hover:!bg-white/30' : ''}`}
                  >
                    <span className="text-2xl">{currentLanguage.flag}</span>
                  </Button>
                  {isLangMenuOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50">
                      {UI_LANGUAGES.map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${
                            currentLanguage.code === lang.code ? 'bg-sky-100 text-sky-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(true)}
                  className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 ${isScrolled ? 'text-slate-600 hover:bg-slate-100' : 'text-white hover:bg-white/20'}`}
                >
                  <span className="sr-only">{t('navbar.open_menu')}</span>
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white text-center py-20 px-4 pt-36">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 leading-tight">
            <p>{t('hero.title1')}</p>
            <p>{t('hero.title2')}</p>
            <p>{t('hero.title3')}</p>
        </h1>
        <h2 className="text-4xl sm:text-3xl md:text-3xl font-extrabold mb-4 leading-tight">{t('hero.subtitle')}</h2>
        {/* Language Flags */}
        <div className="flex justify-center items-center my-8">
          <p className="text-sm text-sky-200/90 mr-4 hidden sm:block">{t('hero.supported_languages')}:</p>
          <div className="flex items-center -space-x-2">
            <span className="h-10 w-10 rounded-full border-2 border-white/50 bg-white/20 flex items-center justify-center text-2xl shadow-md transition-transform hover:scale-110" title="Srpski" role="img" aria-label="Serbian flag">🇷🇸</span>
            <span className="h-10 w-10 rounded-full border-2 border-white/50 bg-white/20 flex items-center justify-center text-2xl shadow-md transition-transform hover:scale-110" title="Nemački" role="img" aria-label="German flag">🇩🇪</span>
            <span className="h-10 w-10 rounded-full border-2 border-white/50 bg-white/20 flex items-center justify-center text-2xl shadow-md transition-transform hover:scale-110" title="Engleski" role="img" aria-label="United Kingdom flag">🇬🇧</span>
            <div className="h-10 w-10 rounded-full border-2 border-white/50 bg-white/20 flex items-center justify-center text-white font-bold text-sm shadow-md transition-transform hover:scale-110" title="{t('hero.other_languages_title')}">
              +10
            </div>
          </div>
        </div>
        
        {/* Kontejner za rotirajući tekst */}
        <div className="h-28 sm:h-24 flex items-center justify-center">
          <p
            key={currentIndex} // Ključ je presudan - tera React da ponovo renderuje element i pokrene animaciju
            className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto animate-fadeInUp"
          >
            <span className="inline-flex items-center bg-gradient-to-r from-sky-200 via-indigo-200 to-sky-200 bg-clip-text text-transparent [text-shadow:0_2px_4px_rgba(0,0,0,0.1)] bg-[200%_auto] animate-shimmer">
              {t(rotatingTextsKeys[currentIndex])}
            </span>
          </p>
        </div>
        <SignUpButton mode="modal">
          <Button size="lg" className="hover:bg-white hover:text-sky-600 font-bold hover:bg-gray-100 transition duration-300 mt-4">
            {t('hero.start_free_button')}
          </Button>
        </SignUpButton>
      </section>

      {/* Video Intro Section */}
      {/* <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-video rounded-2xl shadow-2xl overflow-hidden">
            <iframe
              className="w-full h-full" 
              style={{ border: 'none' }}
              src="https://www.youtube.com/embed/dQw4w9WgXcQ?si=example"
              title="Uvodni video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section> */}

      {/* How-to Section */}
      <section id="how-it-works" className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-slate-800">{t('how_it_works.title')}</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-20 max-w-2xl mx-auto">{t('how_it_works.subtitle')}</p>
          <div className="relative grid md:grid-cols-3 gap-y-16 md:gap-x-8">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-10 left-0 w-full h-px -z-0" aria-hidden="true">
              <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 800 1">
                <path d="M 150 0.5 H 650" stroke="#d1d5db" strokeWidth="2" strokeDasharray="8 8" />
              </svg>
            </div>

            {/* Step 1 */}
            <div className="relative flex flex-col items-center bg-slate-50 px-4">
              <div className="flex items-center justify-center h-20 w-20 rounded-full bg-sky-500 text-white text-3xl font-bold ring-8 ring-slate-100 mb-6 z-10">1</div>
              <h3 className="text-xl font-bold mb-2">{t('how_it_works.step_1_title')}</h3>
              <p className="text-gray-600 max-w-xs">{t('how_it_works.step_1_description')}</p>
            </div>
            {/* Step 2 */}
            <div className="relative flex flex-col items-center bg-slate-50 px-4">
              <div className="flex items-center justify-center h-20 w-20 rounded-full bg-sky-500 text-white text-3xl font-bold ring-8 ring-slate-100 mb-6 z-10">2</div>
              <h3 className="text-xl font-bold mb-2">{t('how_it_works.step_2_title')}</h3>
              <p className="text-gray-600 max-w-xs">{t('how_it_works.step_2_description')}</p>
            </div>
            {/* Step 3 */}
            <div className="relative flex flex-col items-center bg-slate-50 px-4">
              <div className="flex items-center justify-center h-20 w-20 rounded-full bg-sky-500 text-white text-3xl font-bold ring-8 ring-slate-100 mb-6 z-10">3</div>
              <h3 className="text-xl font-bold mb-2">{t('how_it_works.step_3_title')}</h3>
              <p className="text-gray-600 max-w-xs">{t('how_it_works.step_3_description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners/Trusted By Section */}
      {/* <section id="partners" className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-lg font-semibold text-slate-600 tracking-wider uppercase">{t('powered_by.title')}</h2>
          <div 
            className="mt-10 w-full inline-flex flex-nowrap overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)]"
          >
            <ul className="flex items-center justify-center md:justify-start [&_li]:mx-8 [&_img]:max-w-none animate-infinite-scroll group-hover:paused">
              {techLogos.map((tech) => (
                <li key={tech.name}>
                  <img src={tech.logoUrl} alt={tech.name} className="h-8 sm:h-10 w-auto object-contain filter grayscale" />
                </li>
              ))}
            </ul>
            <ul className="flex items-center justify-center md:justify-start [&_li]:mx-8 [&_img]:max-w-none animate-infinite-scroll" aria-hidden="true">
              {techLogos.map((tech) => (
                <li key={`${tech.name}-clone`}>
                  <img src={tech.logoUrl} alt={tech.name} className="h-8 sm:h-10 w-auto object-contain filter grayscale" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section> */}

      {/* NOVO: Sharing Section */}
      <section id="sharing" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Leva strana - Vizuelni prikaz */}
          <div className="flex justify-center md:perspective-1000">
            <div className="relative w-72 h-[525px] sm:w-80 sm:h-[580px] bg-slate-800 border-[14px] border-slate-800 rounded-[40px] shadow-2xl md:transform md:rotate-x-10 md:-rotate-y-10 transition-transform duration-500 hover:!rotate-0">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-800 rounded-b-lg"></div>
              {/* Sadržaj telefona */}
              <div className="p-4 h-full flex flex-col bg-white rounded-[26px] overflow-hidden">
                <h3 className="font-bold text-center text-lg text-slate-800">{t('sharing.solution_title')}</h3>
                <div className="text-sm text-slate-600 mt-4 space-y-3 flex-grow">
                    <p><span className="font-bold text-slate-700">{t('sharing.step_1_prefix')}:</span> {t('sharing.step_1_text')}</p>
                    <p><span className="font-bold text-slate-700">{t('sharing.step_2_prefix')}:</span> {t('sharing.step_2_text')}</p>
                    <p><span className="font-bold text-slate-700">{t('sharing.step_3_prefix')}:</span> {t('sharing.step_3_text')}</p>
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mt-4">
                      <p className="font-bold text-sky-800">{t('sharing.final_solution_prefix')}: x = 5</p>
                    </div>
                </div>
                {/* Dugme za deljenje */}
                <div className="mt-auto">
                    <Button 
                      onClick={handleShareClickDemo}
                      className={`w-full flex items-center justify-center space-x-2 transition-colors duration-300 ${isShared ? 'bg-green-500 hover:bg-green-600' : ''}`}
                    >
                      {isShared ? (
                        <>
                          <CheckIcon className="h-5 w-5" />
                          <span>{t('sharing.link_copied')}</span>
                        </>
                      ) : (
                        <>
                          <ShareIcon className="h-5 w-5" />
                          <span>{t('sharing.share_solution')}</span>
                        </>
                      )}
                    </Button>
                    <div className="flex justify-center space-x-3 mt-3">
                        {/* Ikonice društvenih mreža */}
                        <XSocialIcon className={`w-5 h-5 text-slate-400 transition-all duration-300 ${isShared ? 'text-sky-500 scale-125' : 'hover:text-slate-600'}`} />
                        <FacebookIcon className={`w-5 h-5 text-slate-400 transition-all duration-300 delay-100 ${isShared ? 'text-sky-500 scale-125' : 'hover:text-slate-600'}`} />
                        <InstagramIcon className={`w-5 h-5 text-slate-400 transition-all duration-300 delay-200 ${isShared ? 'text-sky-500 scale-125' : 'hover:text-slate-600'}`} />
                    </div>
                </div>
              </div>
            </div>
          </div>
          {/* Desna strana - Tekstualno objašnjenje */}
          <div className="text-center md:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-slate-800">{t('sharing.title')}</h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6">
              {t('sharing.description')}
            </p>
            <ul className="space-y-4">
              <li className="flex items-center justify-center md:justify-start">
                <UserCircleIcon className="h-6 w-6 text-sky-500 mr-3 flex-shrink-0" />
                <span>{t('sharing.list_item_1')}</span>
              </li>
              <li className="flex items-center justify-center md:justify-start">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-sky-500 mr-3 flex-shrink-0" />
                <span>{t('sharing.list_item_2')}</span>
              </li>
              <li className="flex items-center justify-center md:justify-start">
                <ShareIcon className="h-6 w-6 text-sky-500 mr-3 flex-shrink-0" />
                <span>{t('sharing.list_item_3')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
      {/* Comparison Section Refactored */}
        <section id="comparison" className="py-20 px-4 bg-slate-50">
            <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-slate-800">{t('comparison.title')}</h2>
                {/* Mobile View: Cards */}
                <div className="space-y-4 md:hidden">
                    {comparisonDataKeys.map((item, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-left">
                            <h3 className="font-bold text-lg text-slate-800 mb-4">{t(item.featureKey)}</h3>
                            <div className="flex justify-between items-center border-t border-slate-200 pt-3">
                                <span className="font-semibold text-sky-600">{t('comparison.ai_klikero')}</span>
                                <span className={`font-bold text-xl ${item.klikero === '✔️' ? 'text-green-500' : ''}`}>{item.klikero}</span>
                            </div>
                            <div className="flex justify-between items-center mt-3 border-t border-slate-200 pt-3">
                                <span className="font-semibold text-slate-600">{t('comparison.generic_ai')}</span>
                                <span className={`font-bold ${item.chatGPT === '❌' ? 'text-red-500 text-xl' : 'text-slate-700'}`}>{item.chatGPT}</span>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Desktop View: Table */}
                <div className="hidden md:block shadow-lg rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-100">
                            <tr>
                                <th className="p-4 font-semibold">{t('comparison.functionality')}</th>
                                <th className="p-4 font-semibold text-center text-sky-600">{t('comparison.ai_klikero')}</th>
                                <th className="p-4 font-semibold text-center">{t('comparison.generic_ai')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {comparisonDataKeys.map((item, index) => (
                                <tr key={index}>
                                    <td className="p-4 font-medium">{t(item.featureKey)}</td>
                                    <td className={`p-4 text-center font-bold text-xl ${item.klikero === '✔️' ? 'text-green-500' : ''}`}>{item.klikero}</td>
                                    <td className={`p-4 text-center font-bold ${item.chatGPT === '❌' ? 'text-red-500 text-xl' : 'text-slate-700'}`}>{item.chatGPT}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">{t('testimonials.title')}</h2>
          <p className="text-base sm:text-lg text-gray-600 text-center mb-12 sm:mb-16 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
          <div className="relative max-w-3xl mx-auto">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-2 sm:px-4">
                    <TestimonialCard testimonial={testimonial} />
                  </div>
                ))}
              </div>
            </div>
            {/* Carousel Controls */}
            <button
              onClick={prevTestimonial}
              className="absolute top-1/2 -left-4 md:-left-16 transform -translate-y-1/2 bg-white/60 hover:bg-white rounded-full p-2 shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
              aria-label={t('testimonials.previous_testimonial')}
            >
              <ChevronLeftIcon className="h-6 w-6 text-slate-700" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute top-1/2 -right-4 md:-right-16 transform -translate-y-1/2 bg-white/60 hover:bg-white rounded-full p-2 shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
              aria-label={t('testimonials.next_testimonial')}
            >
              <ChevronRightIcon className="h-6 w-6 text-slate-700" />
            </button>
            {/* Carousel Dots */}
            <div className="flex justify-center space-x-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === index ? 'bg-sky-600 scale-110' : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={t('testimonials.go_to_testimonial', { index: index + 1 })}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4" aria-labelledby="pricing-heading">
        <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-bold text-center mb-4">{t('pricing.title')}</h2>
        <p className="text-base sm:text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            <div className="lg:col-span-2">
                <PricingCard 
                    titleKey="pricing.free_start_title"
                    price="0€"
                    creditsKey="pricing.free_start_credits"
                    descriptionKey="pricing.free_start_description"
                    buttonTextKey="pricing.register_free_button"
                />
            </div>
            <PricingCard titleKey="pricing.top_up_title" price="5€" creditsKey="pricing.top_up_5_credits" descriptionKey="pricing.top_up_5_description" buttonTextKey="pricing.top_up_button" />
            <PricingCard titleKey="pricing.top_up_title" price="10€" creditsKey="pricing.top_up_10_credits" descriptionKey="pricing.top_up_10_description" buttonTextKey="pricing.top_up_button" primary={true} />
            <PricingCard titleKey="pricing.top_up_title" price="20€" creditsKey="pricing.top_up_20_credits" descriptionKey="pricing.top_up_20_description" buttonTextKey="pricing.top_up_button" />
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">{t('faq.title')}</h2>
          <p className="text-base sm:text-lg text-gray-600 text-center mb-12">
            {t('faq.subtitle')}
          </p>
          <div className="divide-y divide-gray-200">
            {faqOptions.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final Call to Action Section */}
      <section id="cta" className="bg-slate-50">
        <div className="max-w-4xl mx-auto py-20 px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-800">
            {t('cta.title')}
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            <Trans
              i18nKey="cta.subtitle"
              values={{ credits: 500 }}
              components={[
                <strong className="font-bold uppercase" />
              ]}
            />
          </p>
          <div className="mt-10">
            <SignUpButton mode="modal">
              <Button size="lg" className="transform transition-transform hover:scale-105">
                {t('cta.button')}
              </Button>
            </SignUpButton>
            <p className="mt-4 text-sm text-slate-500">{t('cta.no_subscription_note')}</p>
          </div>
        </div>
      </section>
      
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        aria-hidden={!isMenuOpen}
      >
        <div className="fixed inset-0 bg-black/50" onClick={() => setIsMenuOpen(false)} />
        <div
          id="mobile-menu"
          className={`absolute top-0 right-0 h-full w-full max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-between items-center p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-sky-700">{t('mobile_menu.title')}</h2>
            <button onClick={() => setIsMenuOpen(false)} className="p-2 -mr-2 rounded-md text-slate-600 hover:bg-slate-100">
              <span className="sr-only">{t('mobile_menu.close_button')}</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="p-4 flex flex-col space-y-2">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} onClick={handleMobileLinkClick} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                {t(link.labelKey)}
              </a>
            ))}
            <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
              <SignInButton mode="modal">
                <Button variant="secondary" className="w-full">
                  {t('auth.login')}
                </Button>
              </SignInButton>
              <SignUpButton mode="modal">
                <Button className="w-full">
                  {t('auth.register')}
                </Button>
              </SignUpButton>
            </div>
          </nav>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="bg-slate-800 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
            <div className="space-y-4">
                <Logo
                  as="h3"
                  textSize="text-xl"
                  textClassName="text-white"
                />
                <p className="text-slate-400">{t('footer.tagline')}</p>
            </div>
            <div>
                <h4 className="font-semibold text-slate-200 tracking-wider uppercase">{t('footer.navigation_title')}</h4>
                <ul className="mt-4 space-y-2 text-slate-400">
                    <li><a href="#how-it-works" className="hover:text-white transition-colors">{t('navbar.how_it_works')}</a></li>
                    <li><a href="#testimonials" className="hover:text-white transition-colors">{t('navbar.testimonials')}</a></li>
                    <li><a href="#pricing" className="hover:text-white transition-colors">{t('navbar.pricing')}</a></li>
                    <li><a href="#faq" className="hover:text-white transition-colors">{t('navbar.faq')}</a></li>
                    <li><a href="#contact" className="hover:text-white transition-colors">{t('navbar.contact')}</a></li>
                </ul>
            </div>
            <div>
                <h4 className="font-semibold text-slate-200 tracking-wider uppercase">{t('footer.legal_info_title')}</h4>
                <ul className="mt-4 space-y-2 text-slate-400">
                    <li><a href={t('helperMenu.termsOfUseLink')} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.terms_of_service')}</a></li>
                    <li><a href={t('helperMenu.privacyPolicyLink')} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.privacy_policy')}</a></li>
                    <li><a href={t('helperMenu.privacyPolicyLink')} target="_blank" className="hover:text-white transition-colors">{t('footer.cookie_policy')}</a></li>
                </ul>
            </div>
            <div>
                <h4 className="font-semibold text-slate-200 tracking-wider uppercase">{t('footer.follow_us_title')}</h4>
                <div className="mt-4 flex space-x-4">
                    <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="Facebook"><FacebookIcon className="h-6 w-6" /></a>
                    <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="X"><XSocialIcon className="h-6 w-6" /></a>
                    <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="Instagram"><InstagramIcon className="h-6 w-6" /></a>
                    <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="LinkedIn"><LinkedInIcon className="h-6 w-6" /></a>
                </div>
            </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-700 text-center text-slate-500 text-xs">
            <p>&copy; {new Date().getFullYear()} AI Klikero. {t('footer.all_rights_reserved')}.</p>
            <p className="mt-1">{t('footer.designed_with_love')}.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;